/**
 * HairstyleListPage
 *
 * Displays all hairstyle reels with status badges (Draft / Ready).
 * Includes dropdown filter to focus on specific status.
 */

import { useState, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Scissors, Clock, CheckCircle, AlertCircle, Loader2, Layers } from 'lucide-react';

// State labels in Russian (user-facing)
const STATE_LABELS: Record<string, string> = {
  topic_selection: 'Выбор темы',
  script_approval: 'Одобрение скрипта',
  awaiting_script_feedback: 'Ожидание правок скрипта',
  image_generation: 'Генерация изображений',
  image_approval: 'Одобрение изображений',
  awaiting_image_feedback: 'Ожидание правок изображений',
  video_generation: 'Генерация видео',
  video_approval: 'Одобрение видео',
  awaiting_video_feedback: 'Ожидание правок видео',
  tts_generation: 'Генерация озвучки',
  muxing: 'Сборка сцен',
  final_assembly: 'Финальная сборка',
  final_approval: 'Финальное одобрение',
  awaiting_final_feedback: 'Ожидание финальных правок',
  done: 'Готово',
};

function getStateIcon(state: string) {
  if (state === 'done') {
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  }
  if (state.includes('awaiting')) {
    return <Clock className="h-4 w-4 text-amber-500" />;
  }
  if (state.includes('generation') || state === 'muxing' || state === 'final_assembly') {
    return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
  }
  if (state.includes('approval')) {
    return <AlertCircle className="h-4 w-4 text-purple-500" />;
  }
  return <Clock className="h-4 w-4 text-foreground-muted" />;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('ru-RU', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface IHairstyleReelItem {
  _id: string;
  _creationTime: number;
  status: string;
  state: string;
  topicRu?: string;
  totalDurationSec?: number;
  sceneCount?: number;
  createdAt: number;
  finalVideoUrl?: string;
}

type StatusFilter = 'all' | 'draft' | 'ready';

export const HairstyleListPage = () => {
  // Omitting status returns all hairstyle reels
  const reels = useQuery(api.hairstyleReels.getByStatus, {}) as IHairstyleReelItem[] | undefined;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const isLoading = reels === undefined;
  const isEmpty = reels !== undefined && reels.length === 0;

  // Filter by status
  const filteredReels = useMemo(() => {
    if (!reels) return [];
    if (statusFilter === 'all') return reels;
    return reels.filter(r => r.status === statusFilter);
  }, [reels, statusFilter]);

  return (
    <div className="h-full bg-background">
      <div className="p-6">
        {/* Header with Status Filter */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Hairstyle</h1>
            <p className="text-sm text-foreground-muted mt-1">
              Hairstyle reels managed via Telegram
            </p>
          </div>
          <Select
            value={statusFilter}
            onValueChange={(value: StatusFilter) => setStatusFilter(value)}
          >
            <SelectTrigger size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="ready">Ready to publish</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!isLoading && isEmpty && (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Scissors className="h-12 w-12 text-foreground-muted mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">No hairstyle reels yet</h3>
            <p className="text-sm text-foreground-muted">
              Send /hair in Telegram to start a new hairstyle reel.
            </p>
          </div>
        )}

        {/* No Results After Filter */}
        {!isLoading && !isEmpty && filteredReels.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Scissors className="h-12 w-12 text-foreground-muted mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">No matching reels</h3>
            <p className="text-sm text-foreground-muted">Try changing the status filter.</p>
          </div>
        )}

        {/* Reel Cards */}
        {!isLoading && filteredReels.length > 0 && (
          <div className="space-y-3">
            {filteredReels.map(reel => (
              <div
                key={reel._id}
                className="p-4 bg-background-card rounded-lg border border-border transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Topic */}
                    <div className="flex items-center gap-2.5">
                      <h3 className="text-sm font-medium text-foreground truncate">
                        {reel.topicRu || 'Тема не выбрана'}
                      </h3>

                      {/* Ready Status Badge */}
                      {reel.status === 'ready' ? (
                        <Badge className="shrink-0 border-green-500/20 bg-green-500/10 text-green-600 dark:text-green-400">
                          Ready to publish
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="shrink-0">
                          Draft
                        </Badge>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-3 mt-2 text-xs text-foreground-muted">
                      <span>{formatDate(reel.createdAt)}</span>
                      {reel.totalDurationSec && (
                        <>
                          <span>&middot;</span>
                          <span>{reel.totalDurationSec} сек</span>
                        </>
                      )}
                      {reel.sceneCount && (
                        <>
                          <span>&middot;</span>
                          <span className="flex items-center gap-1">
                            <Layers className="h-3 w-3" />
                            {reel.sceneCount} сцен
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* State Badge */}
                  <div className="flex items-center gap-1.5 ml-4 shrink-0">
                    {getStateIcon(reel.state)}
                    <span className="text-xs text-foreground-muted">
                      {STATE_LABELS[reel.state] || reel.state}
                    </span>
                  </div>
                </div>

                {/* Final video player */}
                {reel.state === 'done' && reel.finalVideoUrl && (
                  <div className="mt-3">
                    <video
                      src={reel.finalVideoUrl}
                      controls
                      className="w-full max-w-xs rounded-lg"
                      preload="metadata"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
